import React, { useState, useEffect, useRef } from 'react';
import { Report } from '../../types/report.types';
import { DataSet, ColumnId } from '../../types';
import * as styles from '../styles/components/report-actions.css.ts';

/**
 * ReportActions - 汎用的なレポート操作コンポーネント
 * 
 * 任意の図表要素（散布図、ヒストグラム等）にレポート機能を追加します。
 * 右クリックでコンテキストメニューを表示し、レポートへの追加/作成を行います。
 */

// レポート操作のデータインターフェース
export interface ReportActionData {
  type: 'scatter_plot' | 'histogram' | 'line_chart' | 'bar_chart' | 'time_series' | 'time_series_plot' | 'multi_time_series' | 'multi_time_series_plot' | 'qq_plot' | 'residual_plot' | 'text' | 'linear_regression' | 'statistics_summary';
  title: string;
  // 散布図用のデータ
  scatterPlotData?: {
    xColumn: ColumnId;
    yColumn: ColumnId;
    dataset: DataSet;
  };
  // ヒストグラム用のデータ  
  histogramData?: {
    columnName: ColumnId;
    bins: number;
    dataset: DataSet;
  };
  // 時系列プロット用のデータ
  timeSeriesData?: {
    dateColumn: ColumnId;
    valueColumn: ColumnId;
    dataset: DataSet;
  };
  // マルチ系列時系列プロット用のデータ
  multiTimeSeriesData?: {
    dateColumn: ColumnId;
    valueColumns: ColumnId[];
    dataset: DataSet;
  };
  // QQプロット用のデータ
  qqPlotData?: {
    columnName: ColumnId;
    dataset: DataSet;
  };
  // 残差プロット用のデータ
  residualPlotData?: {
    predictedColumn: ColumnId;
    residualColumn: ColumnId;
    dataset: DataSet;
  };
  // テキストメモ用のデータ
  textData?: {
    content: string;
    formatting?: {
      fontSize?: number;
      fontFamily?: string;
      color?: string;
      backgroundColor?: string;
      textAlign?: 'left' | 'center' | 'right';
      bold?: boolean;
      italic?: boolean;
    };
  };
  // 線形回帰用のデータ
  linearRegressionData?: {
    xColumns: ColumnId[];
    yColumn: ColumnId;
    includeIntercept: boolean;
    dataset: DataSet;
  };
  // 統計サマリー用のデータ
  statisticsSummaryData?: {
    targetColumns: string[];
    datasetId: string;
  };
  // 他の図表タイプ用のデータを将来追加可能
}

interface ReportActionsProps {
  children: React.ReactNode;
  availableReports: Report[];
  onAddToReport?: (reportId: string, data: ReportActionData) => void;
  onCreateNewReport?: (data: ReportActionData) => void;
  actionData: ReportActionData;
}

export function ReportActions({
  children,
  availableReports,
  onAddToReport,
  onCreateNewReport,
  actionData
}: ReportActionsProps) {
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
    setContextMenuVisible(true);
  };


  const handleAddToReport = (reportId: string) => {
    if (onAddToReport) {
      onAddToReport(reportId, actionData);
    }
    setContextMenuVisible(false);
  };

  const handleCreateNewReport = () => {
    if (onCreateNewReport) {
      onCreateNewReport(actionData);
    }
    setContextMenuVisible(false);
  };

  // コンテキストメニュー外のクリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setContextMenuVisible(false);
      }
    };

    if (contextMenuVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [contextMenuVisible]);

  return (
    <div style={{ position: 'relative' }} onContextMenu={handleContextMenu}>
      {children}
      
      {/* コンテキストメニュー */}
      {contextMenuVisible && (
        <div
          className={styles.contextMenu}
          style={{
            position: 'fixed',
            left: contextMenuPosition.x,
            top: contextMenuPosition.y,
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #cbd5e0',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            minWidth: '200px',
            maxWidth: '300px',
            padding: 0,
            fontSize: '13px'
          }}
          ref={menuRef}
        >
          {/* メニューヘッダー */}
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#f7fafc',
            fontWeight: 'bold',
            color: '#2d3748'
          }}>
            {actionData.title}
          </div>

          {/* レポート追加セクション */}
          <div style={{ padding: '8px 0' }}>
            {/* 新規レポート作成 */}
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCreateNewReport();
              }}
              style={{
                width: '100%',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#2b6cb0',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#bee3f8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              + Create New Report
            </button>

            {/* 既存レポート一覧 */}
            {availableReports.length > 0 && (
              <>
                <div style={{
                  padding: '4px 12px',
                  fontSize: '11px',
                  color: '#718096',
                  fontWeight: '500',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Add to Existing Report
                </div>
                {availableReports.map(report => (
                  <button
                    key={report.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddToReport(report.id);
                    }}
                    style={{
                      width: '100%',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#4a5568'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f7fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    📊 {report.name}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* 図表情報セクション */}
          <div style={{
            padding: '8px 12px',
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#f7fafc',
            fontSize: '11px',
            color: '#718096'
          }}>
            {actionData.type === 'scatter_plot' && actionData.scatterPlotData && (
              <>
                <div>Type: Scatter Plot</div>
                <div>X-axis: {actionData.scatterPlotData.xColumn}</div>
                <div>Y-axis: {actionData.scatterPlotData.yColumn}</div>
              </>
            )}
            {actionData.type === 'histogram' && actionData.histogramData && (
              <>
                <div>Type: Histogram</div>
                <div>Column: {actionData.histogramData.columnName}</div>
                <div>Bins: {actionData.histogramData.bins}</div>
              </>
            )}
            {actionData.type === 'time_series' && actionData.timeSeriesData && (
              <>
                <div>Type: Time Series</div>
                <div>Date: {actionData.timeSeriesData.dateColumn}</div>
                <div>Value: {actionData.timeSeriesData.valueColumn}</div>
              </>
            )}
            {actionData.type === 'multi_time_series' && actionData.multiTimeSeriesData && (
              <>
                <div>Type: Multi-Series Time Series</div>
                <div>Date: {actionData.multiTimeSeriesData.dateColumn}</div>
                <div>Values: {actionData.multiTimeSeriesData.valueColumns.join(', ')}</div>
              </>
            )}
            {actionData.type === 'qq_plot' && actionData.qqPlotData && (
              <>
                <div>Type: Q-Q Plot</div>
                <div>Column: {actionData.qqPlotData.columnName}</div>
              </>
            )}
            {actionData.type === 'residual_plot' && actionData.residualPlotData && (
              <>
                <div>Type: Residual Plot</div>
                <div>Predicted: {actionData.residualPlotData.predictedColumn}</div>
                <div>Residual: {actionData.residualPlotData.residualColumn}</div>
              </>
            )}
            {actionData.type === 'statistics_summary' && actionData.statisticsSummaryData && (
              <>
                <div>Type: Statistics Summary</div>
                <div>Columns: {actionData.statisticsSummaryData.targetColumns.join(', ')}</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
